import type { TaxPaymentSettingsGateway } from './TaxPaymentSettingsGateway';
import type {
  AddStateTaxEntryRequest, EmployerTaxDetailEntry, EmployerTaxDetails, SaveEmployerTaxDetailRequest, StateTaxComponentDefinition, StateTaxEntry,
  StateTaxHistoryItem, StateTaxSchema, StateTaxSetup, TaxJurisdictionRow, TaxPaymentSettingsOverview,
} from './types';
import { federalDepositScheduleOptions, filingFrequencyOptions } from './types';
import { DRAFT_STATE_REGISTRY, type DraftState } from './draftStateRegistry';

/** Deterministic synthetic overview for development and tests (the real rules live on the server). */
const mask = (v: string | null | undefined) => (!v ? null : v.length <= 4 ? '••••' : `••••${v.slice(-4)}`);

/** The California setup shape (the real one comes from the server). Structure only: every value below is synthetic and entered, never defaulted. */
const CA_SCHEMA: StateTaxSchema = {
  components: [
    { key: 'SUI', name: 'State Unemployment Insurance', abbreviation: 'SUI', kind: 'rate', fieldLabel: 'SUI Rate (%)', tableLabel: 'SUI Rate', options: [] },
    { key: 'ETT', name: 'Employment Training Tax', abbreviation: 'ETT', kind: 'rate', fieldLabel: 'ETT Rate (%)', tableLabel: 'ETT Rate', options: [] },
    { key: 'SDI', name: 'State Disability Insurance', abbreviation: 'SDI', kind: 'rate', fieldLabel: 'SDI Rate (%)', tableLabel: 'SDI Rate', options: [] },
    {
      key: 'DEPOSIT', name: 'Tax Deposit Schedule', abbreviation: 'Deposit', kind: 'schedule', fieldLabel: 'Deposit Schedule', tableLabel: 'Deposit Schedule',
      options: [
        { code: 'NextDay', label: 'Next-Day' }, { code: 'Semiweekly', label: 'Semiweekly' }, { code: 'Monthly', label: 'Monthly' }, { code: 'Quarterly', label: 'Quarterly' },
      ],
    },
  ],
  tabs: [
    { key: 'sui-ett', label: 'SUI / ETT', componentKeys: ['SUI', 'ETT'] },
    { key: 'deposit', label: 'Tax Deposit Schedule', componentKeys: ['DEPOSIT'] },
    { key: 'sdi', label: 'SDI', componentKeys: ['SDI'] },
  ],
  disclosure: 'Values are recorded as you state them. EPN does not check them against state agency rules, and payroll calculation does not read them yet.',
};

/** The Nevada setup shape (reference set S01-S07): SUI and the Modified Business Tax. Structure only; nothing here is a rate. */
const NV_SCHEMA: StateTaxSchema = {
  components: [
    { key: 'SUI', name: 'State Unemployment Insurance', abbreviation: 'SUI', kind: 'rate', fieldLabel: 'SUI Rate (%)', tableLabel: 'SUI Rate', options: [] },
    {
      key: 'MBT', name: 'Modified Business Tax', abbreviation: 'MBT', kind: 'rate', fieldLabel: 'MBT Rate (%)', tableLabel: 'MBT Rate', options: [],
      missingAdvisory: 'MBT rate not yet confirmed with the NV Dept. of Taxation. Add the confirmed rate to clear this warning.',
    },
  ],
  tabs: [
    { key: 'sui', label: 'SUI', componentKeys: ['SUI'] },
    { key: 'mbt', label: 'Modified Business Tax', componentKeys: ['MBT'] },
  ],
  disclosure: CA_SCHEMA.disclosure,
};

/** The Arizona setup shape (reference set S01-S06): SUI and Withholding. Structure only; nothing here is a rate. */
const AZ_SCHEMA: StateTaxSchema = {
  components: [
    { key: 'SUI', name: 'State Unemployment Insurance', abbreviation: 'SUI', kind: 'rate', fieldLabel: 'SUI Rate (%)', tableLabel: 'SUI Rate', options: [] },
    { key: 'WH', name: 'Withholding', abbreviation: 'WH', kind: 'rate', fieldLabel: 'Rate (%)', tableLabel: 'Rate', options: [], chipLabel: 'Withholding' },
  ],
  tabs: [
    { key: 'sui', label: 'SUI', componentKeys: ['SUI'] },
    { key: 'withholding', label: 'Withholding', componentKeys: ['WH'] },
  ],
  disclosure: CA_SCHEMA.disclosure,
};

/** The Florida setup shape (reference set S01-S05): the Reemployment Tax, shown as "SUI (Reemployment Tax)". Structure only; nothing here is a rate. */
const FL_SCHEMA: StateTaxSchema = {
  components: [
    {
      key: 'SUI', name: 'Reemployment Tax', abbreviation: 'SUI', kind: 'rate', fieldLabel: 'SUI Rate (%)', tableLabel: 'SUI Rate', options: [],
      missingAdvisory: 'Reemployment Tax rate not yet confirmed with the FL Dept. of Revenue. Verify your assigned rate and add it to clear this warning.',
      chipLabel: 'SUI (Reemployment Tax)',
    },
  ],
  tabs: [{ key: 'sui', label: 'SUI (Reemployment Tax)', componentKeys: ['SUI'] }],
  disclosure: CA_SCHEMA.disclosure,
};

/** The Texas setup shape (reference set S01-S04): the TWC unemployment tax, shown as "SUI (TWC)". Structure only; the rate is assigned to each employer. */
const TX_SCHEMA: StateTaxSchema = {
  components: [
    { key: 'SUI', name: 'Texas Workforce Commission', abbreviation: 'SUI', kind: 'rate', fieldLabel: 'SUI Rate (%)', tableLabel: 'SUI Rate', options: [], chipLabel: 'SUI (TWC)' },
  ],
  tabs: [{ key: 'sui', label: 'SUI (TWC)', componentKeys: ['SUI'] }],
  disclosure: CA_SCHEMA.disclosure,
};

/** The New York setup shape (reference set S01-S09): SUI, the NY Paid Family Leave contribution (a rate plus an annual cap) and MCTMT. Structure only. */
const NY_SCHEMA: StateTaxSchema = {
  components: [
    { key: 'SUI', name: 'State Unemployment Insurance', abbreviation: 'SUI', kind: 'rate', fieldLabel: 'SUI Rate (%)', tableLabel: 'SUI Rate', options: [] },
    {
      key: 'PFL', name: 'NY Paid Family Leave', abbreviation: 'NY PFL', kind: 'rate', fieldLabel: 'PFL Rate (%)', tableLabel: 'PFL Rate', options: [],
      capFieldLabel: 'Annual Cap ($)', capTableLabel: 'Annual Cap',
    },
    {
      key: 'MCTMT', name: 'Metropolitan Commuter Transportation Mobility Tax', abbreviation: 'MCTMT', kind: 'rate', fieldLabel: 'MCTMT Rate (%)', tableLabel: 'MCTMT Rate', options: [],
      missingAdvisory: 'MCTMT rate not yet recorded. MCTMT applies only to employers with payroll in the Metropolitan Commuter Transportation District, and a MTA registration may be required. Confirm applicability, then add the rate to clear this warning.',
      isConditional: true,
    },
  ],
  tabs: [
    { key: 'sui', label: 'SUI', componentKeys: ['SUI'] },
    { key: 'pfl', label: 'Special Tax (NY PFL)', componentKeys: ['PFL'] },
    { key: 'mctmt', label: 'MCTMT', componentKeys: ['MCTMT'] },
  ],
  disclosure: CA_SCHEMA.disclosure,
};

interface MockEvent { entry: StateTaxEntry; removedAtUtc?: string; removedBy?: string }

/** The draft setup of one of the other 44 states: the unemployment rate can be recorded, every other program is "Needs Tax Review" and takes no entries. */
function draftSchema(state: DraftState): StateTaxSchema {
  const components: StateTaxComponentDefinition[] = [];
  const tabs: StateTaxSchema['tabs'] = [];
  for (const program of state.programs) {
    if (program.kind === 'employer_rate') {
      components.push({ key: 'SUI', name: program.name, abbreviation: 'SUI', kind: 'rate', fieldLabel: 'SUI Rate (%)', tableLabel: 'SUI Rate', options: [] });
      tabs.push({ key: 'sui', label: 'SUI', componentKeys: ['SUI'] });
    } else {
      tabs.push({ key: program.key, label: program.name, componentKeys: [], reviewStatus: 'Needs Tax Review', description: program.applicability });
    }
  }
  return {
    components, tabs, disclosure: CA_SCHEMA.disclosure, reviewStatus: 'Needs Tax Review',
    reviewNote: `The tax programs listed for ${state.name} are a draft candidate list. They have not been verified against ${state.name}'s agencies or approved by tax review, so nothing in this state is calculated, filed or ready for live payroll. Only the unemployment insurance rate you state can be recorded.`,
  };
}

const DRAFT_SCHEMAS: Record<string, StateTaxSchema> = Object.fromEntries(DRAFT_STATE_REGISTRY.map((s) => [s.code, draftSchema(s)]));
const SCHEMAS: Record<string, StateTaxSchema> = { ...DRAFT_SCHEMAS, CA: CA_SCHEMA, NV: NV_SCHEMA, AZ: AZ_SCHEMA, FL: FL_SCHEMA, TX: TX_SCHEMA, NY: NY_SCHEMA };
const STATE_NAMES: Record<string, string> = {
  ...Object.fromEntries(DRAFT_STATE_REGISTRY.map((s) => [s.code, s.name])),
  CA: 'California', NV: 'Nevada', AZ: 'Arizona', FL: 'Florida', TX: 'Texas', NY: 'New York',
};
const todayIso = () => new Date().toISOString().slice(0, 10);

export class MockTaxPaymentSettingsGateway implements TaxPaymentSettingsGateway {
  private readonly stateEntries = new Map<string, MockEvent[]>();
  private readonly seeded = new Set<string>();
  /** Bumped on every entry added or withdrawn, so a review can tell whether anything changed after it. */
  private readonly versions = new Map<string, number>();
  private readonly reviews = new Map<string, { reviewedAtUtc: string; reviewedBy: string; version: number }>();

  /** Synthetic California history so the drawer is not empty in development. Invented example values only. */
  private seed(key: string) {
    if (this.seeded.has(key)) return;
    this.seeded.add(key);
    if (key === 'NY') {
      this.append(key, { id: 'seed-SUI-2026-01-01', componentKey: 'SUI', ratePercent: 3.1, scheduleCode: null, scheduleLabel: null, effectiveFrom: '2026-01-01',
        recordedAtUtc: '2024-01-01T09:00:00Z', recordedBy: 'a.lincoln@example.test', isCurrent: false, isFuture: false });
      this.append(key, { id: 'seed-PFL-2026-01-01', componentKey: 'PFL', ratePercent: 0.432, annualCapAmount: 411.91, scheduleCode: null, scheduleLabel: null, effectiveFrom: '2026-01-01',
        recordedAtUtc: '2024-01-01T09:00:00Z', recordedBy: 'a.lincoln@example.test', isCurrent: false, isFuture: false });
      return;
    }
    if (key === 'TX') {
      this.append(key, { id: 'seed-SUI-2026-01-01', componentKey: 'SUI', ratePercent: 2.7, scheduleCode: null, scheduleLabel: null, effectiveFrom: '2026-01-01',
        recordedAtUtc: '2024-01-01T09:00:00Z', recordedBy: 'a.lincoln@example.test', isCurrent: false, isFuture: false });
      return;
    }
    if (key === 'AZ') {
      this.append(key, { id: 'seed-SUI-2026-01-01', componentKey: 'SUI', ratePercent: 2, scheduleCode: null, scheduleLabel: null, effectiveFrom: '2026-01-01',
        recordedAtUtc: '2024-01-01T09:00:00Z', recordedBy: 'a.lincoln@example.test', isCurrent: false, isFuture: false });
      this.append(key, { id: 'seed-WH-2024-01-01', componentKey: 'WH', ratePercent: 2, scheduleCode: null, scheduleLabel: null, effectiveFrom: '2024-01-01',
        recordedAtUtc: '2024-01-01T09:00:00Z', recordedBy: 'a.lincoln@example.test', isCurrent: false, isFuture: false });
      this.append(key, { id: 'seed-WH-2026-01-01', componentKey: 'WH', ratePercent: 2.5, scheduleCode: null, scheduleLabel: null, effectiveFrom: '2026-01-01',
        recordedAtUtc: '2025-12-15T10:30:00Z', recordedBy: 'a.lincoln@example.test', isCurrent: false, isFuture: false });
      return;
    }
    if (key === 'NV') {
      this.append(key, { id: 'seed-SUI-2026-01-01', componentKey: 'SUI', ratePercent: 3, scheduleCode: null, scheduleLabel: null, effectiveFrom: '2026-01-01',
        recordedAtUtc: '2024-01-01T09:00:00Z', recordedBy: 'a.lincoln@example.test', isCurrent: false, isFuture: false });
      return;
    }
    if (key !== 'CA') return;
    const add = (componentKey: string, effectiveFrom: string, extra: Partial<StateTaxEntry>) =>
      this.append(key, { id: `seed-${componentKey}-${effectiveFrom}`, componentKey, ratePercent: null, scheduleCode: null, scheduleLabel: null, effectiveFrom,
        recordedAtUtc: '2024-01-01T09:00:00Z', recordedBy: 'a.lincoln@example.test', isCurrent: false, isFuture: false, ...extra });
    add('SUI', '2024-01-01', { ratePercent: 3.4 });
    add('ETT', '2024-01-01', { ratePercent: 0.1 });
    add('SDI', '2024-01-01', { ratePercent: 0.9 });
    add('SDI', '2026-01-01', { ratePercent: 1.1, recordedAtUtc: '2025-12-15T10:30:00Z' });
    add('DEPOSIT', '2024-01-01', { scheduleCode: 'Monthly', scheduleLabel: 'Monthly' });
  }

  private append(key: string, entry: StateTaxEntry) {
    this.stateEntries.set(key, [...(this.stateEntries.get(key) ?? []), { entry }]);
    this.versions.set(key, (this.versions.get(key) ?? 0) + 1);
  }

  private liveEntries(key: string): StateTaxEntry[] {
    this.seed(key);
    return (this.stateEntries.get(key) ?? []).filter((e) => !e.removedAtUtc).map((e) => e.entry);
  }

  private stateView(key: string): StateTaxSetup {
    const schema = SCHEMAS[key] ?? null;
    const today = todayIso();
    const live = this.liveEntries(key);
    const review = this.reviews.get(key);
    return {
      jurisdictionKey: key,
      jurisdiction: STATE_NAMES[key] ?? key,
      schema,
      review: review ? { reviewedAtUtc: review.reviewedAtUtc, reviewedBy: review.reviewedBy, changedSince: (this.versions.get(key) ?? 0) > review.version } : null,
      components: (schema?.components ?? []).map((c) => {
        const own = live.filter((e) => e.componentKey === c.key).sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
        const currentId = own.find((e) => e.effectiveFrom <= today)?.id;
        return { componentKey: c.key, entries: own.map((e) => ({ ...e, isCurrent: e.id === currentId, isFuture: e.effectiveFrom > today })) };
      }),
    };
  }

  async getStateTax(jurisdictionKey: string): Promise<StateTaxSetup> {
    return this.stateView(jurisdictionKey);
  }

  async addStateTaxEntry(jurisdictionKey: string, request: AddStateTaxEntryRequest): Promise<StateTaxSetup> {
    const schema = SCHEMAS[jurisdictionKey];
    if (!schema) throw new Error('Detailed tax setup is not available for this state yet.');
    const component: StateTaxComponentDefinition | undefined = schema.components.find((c) => c.key === request.componentKey);
    if (!component) throw new Error('Unknown tax component for this state.');
    if (!request.effectiveFrom || request.effectiveFrom < '2000-01-01') throw new Error('Enter a valid effective date (from the year 2000, and no more than five years ahead).');
    let ratePercent: number | null = null;
    let annualCapAmount: number | null = null;
    let option: { code: string; label: string } | undefined;
    if (component.capFieldLabel) {
      const cap = request.annualCapAmount;
      if (cap == null || Number.isNaN(cap) || cap <= 0 || cap > 1_000_000) throw new Error(`Enter the ${component.abbreviation} annual cap as a dollar amount above 0 and up to 1,000,000.`);
      if (Math.round(cap * 100) / 100 !== cap) throw new Error(`The ${component.abbreviation} annual cap can have at most two decimal places.`);
      annualCapAmount = cap;
    } else if (request.annualCapAmount != null) {
      throw new Error(`${component.abbreviation} does not take an annual cap.`);
    }
    if (component.kind === 'rate') {
      const v = request.ratePercent;
      if (v == null || Number.isNaN(v) || v < 0 || v > 100) throw new Error(`Enter the ${component.abbreviation} rate as a percentage between 0 and 100.`);
      if (Math.round(v * 10000) / 10000 !== v) throw new Error(`The ${component.abbreviation} rate can have at most four decimal places.`);
      ratePercent = v;
    } else {
      option = component.options.find((o) => o.code === request.scheduleCode);
      if (!option) throw new Error('Choose one of the listed deposit schedules.');
    }
    if (this.liveEntries(jurisdictionKey).some((e) => e.componentKey === component.key && e.effectiveFrom === request.effectiveFrom)) {
      throw new Error('An entry already exists for that effective date. Choose another date; past entries are kept as history.');
    }
    this.append(jurisdictionKey, {
      id: `mock-${Math.random().toString(36).slice(2)}`, componentKey: component.key, ratePercent, annualCapAmount, scheduleCode: option?.code ?? null, scheduleLabel: option?.label ?? null,
      effectiveFrom: request.effectiveFrom, recordedAtUtc: new Date().toISOString(), recordedBy: 'admin@epn.local', isCurrent: false, isFuture: false,
    });
    return this.stateView(jurisdictionKey);
  }

  async reviewStateTax(jurisdictionKey: string): Promise<StateTaxSetup> {
    this.seed(jurisdictionKey);
    const schema = SCHEMAS[jurisdictionKey];
    if (!schema) throw new Error('Detailed tax setup is not available for this state yet.');
    if (schema.reviewStatus) throw new Error(`${STATE_NAMES[jurisdictionKey] ?? jurisdictionKey} is awaiting tax review, so its setup cannot be marked as reviewed yet.`);
    const today = todayIso();
    const live = this.liveEntries(jurisdictionKey);
    if (!schema.components.filter((c) => !c.isConditional).every((c) => live.some((e) => e.componentKey === c.key && e.effectiveFrom <= today))) {
      throw new Error('Record a value in force for every required tax component before marking the setup as reviewed.');
    }
    this.reviews.set(jurisdictionKey, { reviewedAtUtc: new Date().toISOString(), reviewedBy: 'admin@epn.local', version: this.versions.get(jurisdictionKey) ?? 0 });
    return this.stateView(jurisdictionKey);
  }

  async removeStateTaxEntry(jurisdictionKey: string, entryId: string): Promise<StateTaxSetup> {
    this.seed(jurisdictionKey);
    const event = (this.stateEntries.get(jurisdictionKey) ?? []).find((e) => e.entry.id === entryId && !e.removedAtUtc);
    if (!event) throw new Error('That entry does not exist.');
    if (event.entry.effectiveFrom <= todayIso()) {
      throw new Error('Only an entry with a future effective date can be removed. Entries already in force stay as history; add a new entry to change the value.');
    }
    this.versions.set(jurisdictionKey, (this.versions.get(jurisdictionKey) ?? 0) + 1);
    event.removedAtUtc = new Date().toISOString();
    event.removedBy = 'admin@epn.local';
    return this.stateView(jurisdictionKey);
  }

  async getStateTaxHistory(jurisdictionKey: string): Promise<StateTaxHistoryItem[]> {
    this.seed(jurisdictionKey);
    const schema = SCHEMAS[jurisdictionKey];
    const valueOf = (e: StateTaxEntry) => (e.ratePercent !== null ? `${e.ratePercent}%${e.annualCapAmount != null ? ` (annual cap $${e.annualCapAmount.toFixed(2)})` : ''}` : e.scheduleLabel ?? '');
    const abbreviation = (key: string) => schema?.components.find((c) => c.key === key)?.abbreviation ?? key;
    const items: StateTaxHistoryItem[] = [];
    for (const { entry, removedAtUtc, removedBy } of this.stateEntries.get(jurisdictionKey) ?? []) {
      const base = { entryId: entry.id, componentKey: entry.componentKey, componentAbbreviation: abbreviation(entry.componentKey), value: valueOf(entry), effectiveFrom: entry.effectiveFrom };
      items.push({ ...base, action: 'Added', occurredAtUtc: entry.recordedAtUtc, by: entry.recordedBy });
      if (removedAtUtc) items.push({ ...base, action: 'Removed', occurredAtUtc: removedAtUtc, by: removedBy ?? '' });
    }
    return items.sort((a, b) => b.occurredAtUtc.localeCompare(a.occurredAtUtc));
  }

  /** The California row's status follows its entries, like the server: recorded once every component has a value in force. */
  private stateRecorded(key: string): boolean {
    const today = todayIso();
    const live = this.liveEntries(key);
    return (SCHEMAS[key]?.components ?? []).every((c) => live.some((e) => e.componentKey === c.key && e.effectiveFrom <= today));
  }


  private readonly details = new Map<string, { entry: EmployerTaxDetailEntry; withholding: string | null; sui: string | null }[]>();

  private view(key: string): EmployerTaxDetails {
    const rows = [...(this.details.get(key) ?? [])].sort((a, b) => b.entry.effectiveFrom.localeCompare(a.entry.effectiveFrom));
    const today = new Date().toISOString().slice(0, 10);
    return {
      jurisdictionKey: key,
      jurisdiction: key === 'federal' ? 'Federal' : key,
      current: rows.find((r) => r.entry.effectiveFrom <= today)?.entry ?? null,
      history: rows.map((r) => r.entry),
    };
  }

  async getEmployerDetails(jurisdictionKey: string): Promise<EmployerTaxDetails> {
    return this.view(jurisdictionKey);
  }

  async saveEmployerDetails(jurisdictionKey: string, request: SaveEmployerTaxDetailRequest): Promise<EmployerTaxDetails> {
    const list = this.details.get(jurisdictionKey) ?? [];
    if (list.some((r) => r.entry.effectiveFrom === request.effectiveFrom)) {
      throw new Error('An entry already exists for that effective date. Choose a later date; past entries are kept as history.');
    }
    const prev = [...list].sort((a, b) => b.entry.effectiveFrom.localeCompare(a.entry.effectiveFrom)).find((r) => r.entry.effectiveFrom < request.effectiveFrom);
    const carry = (requested: string | null | undefined, before: string | null | undefined) =>
      requested === undefined || requested === null ? before ?? null : requested.trim() === '' ? null : requested.trim();
    const withholding = carry(request.withholdingAccountNumber, prev?.withholding);
    const sui = carry(request.suiAccountNumber, prev?.sui);
    const label = (opts: readonly { value: number; label: string }[], v: number | null | undefined) => opts.find((o) => o.value === v)?.label ?? null;
    list.push({
      withholding, sui,
      entry: {
        effectiveFrom: request.effectiveFrom,
        withholdingAccountNumberMasked: mask(withholding),
        suiAccountNumberMasked: mask(sui),
        federalDepositSchedule: label(federalDepositScheduleOptions, request.federalDepositSchedule) ?? prev?.entry.federalDepositSchedule ?? null,
        filingFrequency: label(filingFrequencyOptions, request.filingFrequency) ?? prev?.entry.filingFrequency ?? null,
        recordedAtUtc: new Date().toISOString(),
      },
    });
    this.details.set(jurisdictionKey, list);
    return this.view(jurisdictionKey);
  }

  async getOverview(): Promise<TaxPaymentSettingsOverview> {
    const loc = (id: string, name: string, address: string, employeesAssigned: number, status = 'Active') => ({ id, name, status, address, employeesAssigned });
    const state = (jurisdictionKey: string, jurisdiction: string, locations: TaxJurisdictionRow['relatedLocations'], setupStatus: string, taxComponents: string[] | null = null): TaxJurisdictionRow => ({
      jurisdictionKey, jurisdiction, setupType: 'State tax', relatedLocations: locations,
      employeesAssigned: locations.reduce((n, l) => n + (l.employeesAssigned ?? 0), 0), setupStatus, taxComponents, hasDetailedSetup: taxComponents !== null, verifiedOn: null,
    });
    const draft = (key: string, name: string, location: string, address: string, employees: number) =>
      state(key, name, [loc(`wl-${key.toLowerCase()}`, location, address, employees)], 'Needs Tax Review', DRAFT_SCHEMAS[key]!.components.map((c) => c.abbreviation));
    const reviewedOn = (key: string) => {
      const review = this.reviews.get(key);
      return review && (this.versions.get(key) ?? 0) <= review.version ? review.reviewedAtUtc.slice(0, 10) : null;
    };
    const withReview = (rows: TaxJurisdictionRow[]) => rows.map((r) => (SCHEMAS[r.jurisdictionKey] ? { ...r, verifiedOn: reviewedOn(r.jurisdictionKey) } : r));
    return {
      rows: withReview([
        { jurisdictionKey: 'federal', jurisdiction: 'Federal', setupType: 'Federal tax', relatedLocations: [], employeesAssigned: 7, setupStatus: 'Setup Needed', verifiedOn: null },
        state('CA', 'California', [loc('wl-sf', 'SF Headquarters', '123 Market St, San Francisco, CA 94105', 48)],
          this.stateRecorded('CA') ? 'Setup Recorded - Not Verified' : 'Setup Needed', CA_SCHEMA.components.map((c) => c.abbreviation)),
        state('NV', 'Nevada', [loc('wl-lv', 'Las Vegas Branch', '456 S Rainbow Blvd, Las Vegas, NV 89107', 12)],
          this.stateRecorded('NV') ? 'Setup Recorded - Not Verified' : 'Setup Needed', NV_SCHEMA.components.map((c) => c.abbreviation)),
        state('AZ', 'Arizona', [loc('wl-phx', 'Phoenix Office', '2500 N Central Ave, Phoenix, AZ 85004', 9)],
          this.stateRecorded('AZ') ? 'Setup Recorded - Not Verified' : 'Setup Needed', AZ_SCHEMA.components.map((c) => c.chipLabel ?? c.abbreviation)),
        state('FL', 'Florida', [loc('wl-mia', 'Miami Office', '1200 Brickell Ave, Miami, FL 33131', 17)],
          this.stateRecorded('FL') ? 'Setup Recorded - Not Verified' : 'Setup Needed', FL_SCHEMA.components.map((c) => c.chipLabel ?? c.abbreviation)),
        draft('WA', 'Washington', 'Seattle Office', '1201 3rd Ave, Seattle, WA 98101', 6),
        draft('OR', 'Oregon', 'Portland Hub', '88 SW 5th Ave, Portland, OR 97204', 0),
        draft('NJ', 'New Jersey', 'Newark Office', '1 Gateway Center, Newark, NJ 07102', 8),
        draft('MN', 'Minnesota', 'Minneapolis Office', '80 S 8th St, Minneapolis, MN 55402', 5),
        draft('OH', 'Ohio', 'Columbus Office', '77 S High St, Columbus, OH 43215', 7),
        draft('TN', 'Tennessee', 'Nashville Office', '401 Commerce St, Nashville, TN 37219', 4),
        state('DC', 'District of Columbia', [loc('wl-dc', 'DC Office', '1 Main St, Washington, DC 20001', 0)], 'Setup Needed - No Employees Assigned Yet'),
        state('TX', 'Texas', [loc('wl-aus', 'Austin Hub', '500 W 5th St, Austin, TX 78701', 31)],
          this.stateRecorded('TX') ? 'Setup Recorded - Not Verified' : 'Setup Needed', TX_SCHEMA.components.map((c) => c.chipLabel ?? c.abbreviation)),
        state('NY', 'New York', [loc('wl-nyc', 'NYC Office', '350 5th Ave, New York, NY 10118', 24)],
          this.stateRecorded('NY') ? 'Setup Recorded - Not Verified' : 'Setup Needed', NY_SCHEMA.components.map((c) => c.chipLabel ?? c.abbreviation)),
        state('GA', 'Georgia', [loc('wl-old', 'Closed Atlanta Hub', '100 Peachtree St, Atlanta, GA 30303', 0, 'Archived')], 'Historical Only', DRAFT_SCHEMAS.GA!.components.map((c) => c.abbreviation)),
      ]),
      locationsNeedingReview: [],
      companyEinLast4: '4821',
    };
  }
}
